import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api, { TRADE } from '../../infrastructure/config/api';
import Button from '../components/common/Button';

const levels = ['Long', 'Short', 'SL', 'TP1', 'TP2', 'TP3'];
const defaultSymbols = 'CVC,HYPE,TAC,TRIA,SAGA,COS,TIA,IRYS,FF,IDOL';
const excludedSymbols = new Set(['ADA', 'AAVE']);
function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function defaultStartDate() {
  const date = new Date();
  date.setMonth(4);
  date.setDate(1);
  return formatDate(date);
}

function formatPnl(value) {
  if (value === null || value === undefined) return 'không có dữ liệu';
  const number = Number(value);
  const prefix = number > 0 ? '+' : '';
  return `${prefix}${number.toFixed(8)}`;
}

function getResultText(value) {
  if (value === null || value === undefined) return '-';
  const number = Number(value);
  if (number > 0) return 'lời';
  if (number < 0 && Math.abs(number) < 0.05) return 'lỗ nhẹ';
  if (number < 0) return 'lỗ';
  return 'hòa';
}

function getResultColor(value) {
  if (value === null || value === undefined) return 'default';
  const number = Number(value);
  if (number > 0) return 'success';
  if (number < 0) return 'error';
  return 'default';
}

function getLevelCount(row, level) {
  if (level === 'Long') return row.longEntries || 0;
  if (level === 'Short') return row.shortEntries || 0;
  if (level === 'SL') return (row.longSl || 0) + (row.shortSl || 0);
  if (level === 'TP1') return (row.longTp1 || 0) + (row.shortTp1 || 0);
  if (level === 'TP2') return (row.longTp2 || 0) + (row.shortTp2 || 0);
  if (level === 'TP3') return (row.longTp3 || 0) + (row.shortTp3 || 0);
  return 0;
}

function normalizeRows(rows) {
  return rows.filter((row) => !excludedSymbols.has(String(row.symbol || '').toUpperCase())).sort((a, b) => {
    if (a.version !== b.version) return a.version.localeCompare(b.version);
    const pnlA = a.pnl === null || a.pnl === undefined ? -Infinity : Number(a.pnl);
    const pnlB = b.pnl === null || b.pnl === undefined ? -Infinity : Number(b.pnl);
    return pnlB - pnlA;
  });
}

function mergeRowsBySymbol(rows) {
  const merged = new Map();

  rows.forEach((row) => {
    const key = String(row.symbol || '').toUpperCase();
    if (!key) return;

    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, { ...row });
      return;
    }

    merged.set(key, {
      ...existing,
      version: row.version || existing.version,
      pnl: existing.pnl ?? row.pnl,
      totalAlerts: (existing.totalAlerts || 0) + (row.totalAlerts || 0),
      longEntries: (existing.longEntries || 0) + (row.longEntries || 0),
      shortEntries: (existing.shortEntries || 0) + (row.shortEntries || 0),
      longSl: (existing.longSl || 0) + (row.longSl || 0),
      shortSl: (existing.shortSl || 0) + (row.shortSl || 0),
      longTp1: (existing.longTp1 || 0) + (row.longTp1 || 0),
      shortTp1: (existing.shortTp1 || 0) + (row.shortTp1 || 0),
      longTp2: (existing.longTp2 || 0) + (row.longTp2 || 0),
      shortTp2: (existing.shortTp2 || 0) + (row.shortTp2 || 0),
      longTp3: (existing.longTp3 || 0) + (row.longTp3 || 0),
      shortTp3: (existing.shortTp3 || 0) + (row.shortTp3 || 0),
    });
  });

  return Array.from(merged.values());
}

const PositionHistoryPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [startDate, setStartDate] = useState(defaultStartDate());
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [version, setVersion] = useState('all');
  const [symbols, setSymbols] = useState(defaultSymbols);
  const [loading, setLoading] = useState(false);
  const [symbolRows, setSymbolRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const hasInvalidDateRange = Boolean(startDate && endDate && startDate > endDate);

  const applyVersionOverrides = async (rows) => {
    const response = await api.get('/api/managed-webhook-slots');
    const managedSymbols = response.data?.data || [];
    const overrides = new Map();
    const currentSymbols = new Set();

    const activeManagedSymbols = managedSymbols.filter((item) => item.is_active !== false);

    activeManagedSymbols.forEach((item) => {
      const normalizedSymbol = String(item.symbol || '').toUpperCase().replace(/USDT$/, '');
      if (normalizedSymbol) {
        overrides.set(normalizedSymbol, String(item.webhook_version || '').toLowerCase());
        currentSymbols.add(normalizedSymbol);
      }
    });

    const overridden = rows
      .filter((row) => currentSymbols.has(String(row.symbol || '').toUpperCase()))
      .map((row) => {
      const normalizedRowSymbol = String(row.symbol || '').toUpperCase();
      const override = overrides.get(normalizedRowSymbol);
      return override === 'v18' || override === 'v19'
        ? { ...row, version: override }
        : { ...row };
      });

    const existingSymbols = new Set(
      overridden.map((row) => String(row.symbol || '').toUpperCase())
    );

    activeManagedSymbols.forEach((item) => {
      const normalizedSymbol = String(item.symbol || '').toUpperCase().replace(/USDT$/, '');
      if (!normalizedSymbol || existingSymbols.has(normalizedSymbol)) return;

      overridden.push({
        version: String(item.webhook_version || 'v18').toLowerCase(),
        symbol: normalizedSymbol,
        pnl: null,
        totalAlerts: 0,
        longEntries: 0,
        shortEntries: 0,
        longSl: 0,
        shortSl: 0,
        longTp1: 0,
        shortTp1: 0,
        longTp2: 0,
        shortTp2: 0,
        longTp3: 0,
        shortTp3: 0,
      });
    });

    return mergeRowsBySymbol(overridden);
  };

  const rows = useMemo(() => normalizeRows(symbolRows), [symbolRows]);
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        if (row.pnl !== null && row.pnl !== undefined) {
          acc.pnl += Number(row.pnl) || 0;
          acc.symbolsWithPnl += 1;
        }
        acc.alerts += Number(row.totalAlerts) || 0;
        return acc;
      },
      { pnl: 0, alerts: 0, symbolsWithPnl: 0 },
    );
  }, [rows]);

  const versionTotals = useMemo(() => {
    return summaryRows.reduce((acc, row) => {
      acc[row.version] = (acc[row.version] || 0) + (Number(row.count) || 0);
      return acc;
    }, {});
  }, [summaryRows]);

  const loadStats = async () => {
    if (!startDate || !endDate) {
      enqueueSnackbar('Chọn đủ start date và end date', { variant: 'warning' });
      return;
    }

    if (hasInvalidDateRange) {
      enqueueSnackbar('Từ ngày phải nhỏ hơn hoặc bằng Đến ngày', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(TRADE.V18_STATS, {
        params: {
          startDate,
          endDate,
          version,
          symbols,
        },
      });
      setSummaryRows(response.data?.data || []);
      setSymbolRows(await applyVersionOverrides(response.data?.symbolStats || []));
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Không tải được thống kê v18/v19', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, sm: 3 } }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
              Thống kê V18 / V19 theo symbol
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Ghi nhận webhook Long/Short, SL, TP1, TP2, TP3 và tổng PnL của user hiện tại trong khoảng ngày.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              label="Từ ngày"
              type="date"
              size="small"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: endDate || undefined }}
              error={hasInvalidDateRange}
            />
            <TextField
              label="Đến ngày"
              type="date"
              size="small"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || undefined }}
              error={hasInvalidDateRange}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Version</InputLabel>
              <Select
                label="Version"
                value={version}
                onChange={(event) => setVersion(event.target.value)}
              >
                <MenuItem value="v19">V19</MenuItem>
                <MenuItem value="v18">V18</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={loadStats} loading={loading} disabled={hasInvalidDateRange}>
              Tải thống kê
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 2 }}>
          <TextField
            fullWidth
            label="Symbols"
            size="small"
            value={symbols}
            onChange={(event) => setSymbols(event.target.value)}
            helperText="Nhập symbol cách nhau bằng dấu phẩy. Các symbol này vẫn hiện kể cả chưa có webhook ở version đang chọn."
          />
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">Tổng PnL</Typography>
              <Typography variant="h5" color={totals.pnl >= 0 ? 'success.main' : 'error.main'} sx={{ mt: 0.5 }}>
                {formatPnl(totals.pnl)} USDT
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">Symbols có PnL</Typography>
              <Typography variant="h5" sx={{ mt: 0.5 }}>{totals.symbolsWithPnl}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">V18 alerts</Typography>
              <Typography variant="h5" sx={{ mt: 0.5 }}>{versionTotals.v18 || 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">V19 alerts</Typography>
              <Typography variant="h5" sx={{ mt: 0.5 }}>{versionTotals.v19 || 0}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Alert severity="info">
          V18 chỉ tính webhook đã qua filter và bỏ filtered/duplicate/error. V19 không qua filter và có thêm TP3.
        </Alert>

        <Paper sx={{ p: { xs: 1, md: 2 } }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell align="right">Tổng PnL</TableCell>
                  <TableCell>Kết quả</TableCell>
                  {levels.map((level) => (
                    <TableCell key={level} align="right">{level}</TableCell>
                  ))}
                  <TableCell align="right">Alerts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.version}-${row.symbol}`} hover>
                    <TableCell>
                      <Chip size="small" label={row.version.toUpperCase()} color={row.version === 'v19' ? 'secondary' : 'primary'} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{row.symbol}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: row.pnl > 0 ? 'success.main' : row.pnl < 0 ? 'error.main' : 'text.secondary',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatPnl(row.pnl)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={getResultText(row.pnl)} color={getResultColor(row.pnl)} variant={row.pnl === null || row.pnl === undefined ? 'outlined' : 'filled'} />
                    </TableCell>
                    {levels.map((level) => (
                      <TableCell key={level} align="right">{getLevelCount(row, level)}</TableCell>
                    ))}
                    <TableCell align="right">{row.totalAlerts}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>Không có symbol v18/v19 trong khoảng ngày này.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Container>
  );
};

export default PositionHistoryPage;
